import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { authUtils } from "@/utils/auth";
import {
  Plus, Search, Trash2, Edit2, DollarSign, Gift, Users,
  ChevronDown, ChevronUp, X, Check, RefreshCw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create Promo Code Modal
function CreatePromoCodeModal({ onClose, onCreated }) {
  const [code, setCode] = useState("DMRG-");
  const [holderName, setHolderName] = useState("");
  const [holderEmail, setHolderEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!code || !holderName || !holderEmail) {
      toast.error("Please fill in all fields");
      return;
    }
    if (!code.startsWith("DMRG-")) {
      toast.error("Code must start with 'DMRG-'");
      return;
    }

    setLoading(true);
    try {
      const token = authUtils.getToken();
      await axios.post(
        `${API}/admin/promo-codes`,
        { code, holder_name: holderName, holder_email: holderEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Promo code created!");
      onCreated();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create promo code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="glass rounded-2xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-xl font-bold text-white">Create Promo Code</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-white/60 text-sm block mb-2">Promo Code</label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="DMRG-JACK1"
              className="bg-dream-bg border-dream-border text-white"
            />
            <p className="text-white/40 text-xs mt-1">Format: DMRG-XXXXX</p>
          </div>
          <div>
            <label className="text-white/60 text-sm block mb-2">Holder Name</label>
            <Input
              value={holderName}
              onChange={(e) => setHolderName(e.target.value)}
              placeholder="John Smith"
              className="bg-dream-bg border-dream-border text-white"
            />
          </div>
          <div>
            <label className="text-white/60 text-sm block mb-2">Holder Email</label>
            <Input
              value={holderEmail}
              onChange={(e) => setHolderEmail(e.target.value)}
              placeholder="john@example.com"
              type="email"
              className="bg-dream-bg border-dream-border text-white"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={onClose} variant="outline" className="flex-1 border-dream-border text-white">
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={loading}
            className="flex-1 bg-dream-violet text-white hover:bg-dream-violet-glow"
          >
            {loading ? "Creating..." : "Create"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Redeem Credit Modal
function RedeemCreditModal({ holder, onClose, onRedeemed }) {
  const [amount, setAmount] = useState("");
  const [serviceBooked, setServiceBooked] = useState("");
  const [bookingId, setBookingId] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRedeem = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (parseFloat(amount) > holder.available_balance) {
      toast.error("Amount exceeds available balance");
      return;
    }

    setLoading(true);
    try {
      const token = authUtils.getToken();
      await axios.post(
        `${API}/admin/promo-credits/redeem`,
        {
          holder_email: holder.holder_email,
          amount: parseFloat(amount),
          service_booked: serviceBooked || null,
          booking_id: bookingId || null,
          admin_notes: notes || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`$${amount} credit redeemed for ${holder.holder_name}`);
      onRedeemed();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to redeem credit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="glass rounded-2xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-xl font-bold text-white">Redeem Credit</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        <div className="bg-dream-surface rounded-lg p-4 mb-4">
          <p className="text-white font-medium">{holder.holder_name}</p>
          <p className="text-white/60 text-sm">{holder.holder_email}</p>
          <p className="text-dream-violet font-mono text-lg mt-2">
            Available: ${holder.available_balance?.toFixed(2)}
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-white/60 text-sm block mb-2">Amount to Redeem *</label>
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              type="number"
              step="0.01"
              max={holder.available_balance}
              className="bg-dream-bg border-dream-border text-white"
            />
          </div>
          <div>
            <label className="text-white/60 text-sm block mb-2">Service Booked (optional)</label>
            <Input
              value={serviceBooked}
              onChange={(e) => setServiceBooked(e.target.value)}
              placeholder="e.g., Recording Session"
              className="bg-dream-bg border-dream-border text-white"
            />
          </div>
          <div>
            <label className="text-white/60 text-sm block mb-2">Booking ID (optional)</label>
            <Input
              value={bookingId}
              onChange={(e) => setBookingId(e.target.value)}
              placeholder="e.g., abc-123-xyz"
              className="bg-dream-bg border-dream-border text-white"
            />
          </div>
          <div>
            <label className="text-white/60 text-sm block mb-2">Admin Notes (optional)</label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes..."
              className="bg-dream-bg border-dream-border text-white"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={onClose} variant="outline" className="flex-1 border-dream-border text-white">
            Cancel
          </Button>
          <Button
            onClick={handleRedeem}
            disabled={loading}
            className="flex-1 bg-green-600 text-white hover:bg-green-700"
          >
            {loading ? "Processing..." : `Redeem $${amount || "0"}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Main Promo Code Management Component
export default function PromoCodeManagement() {
  const [promoCodes, setPromoCodes] = useState([]);
  const [credits, setCredits] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState("codes");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedHolder, setSelectedHolder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = authUtils.getToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [codesRes, creditsRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/admin/promo-codes`, { headers }),
        axios.get(`${API}/admin/promo-credits`, { headers }),
        axios.get(`${API}/admin/promo-transactions`, { headers })
      ]);

      setPromoCodes(codesRes.data.promo_codes || []);
      setCredits(creditsRes.data.credits || []);
      setTransactions(transactionsRes.data.transactions || []);
    } catch (error) {
      console.error("Error fetching promo data:", error);
      toast.error("Failed to load promo code data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleToggleStatus = async (code, currentStatus) => {
    const newStatus = currentStatus === "active" ? "disabled" : "active";
    try {
      const token = authUtils.getToken();
      await axios.put(
        `${API}/admin/promo-codes/${code}?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Promo code ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update promo code");
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Are you sure you want to delete promo code ${code}?`)) return;
    
    try {
      const token = authUtils.getToken();
      await axios.delete(`${API}/admin/promo-codes/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Promo code deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete promo code");
    }
  };

  // Filter data based on search
  const filteredCodes = promoCodes.filter(p =>
    p.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.holder_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.holder_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCredits = credits.filter(c =>
    c.holder_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.holder_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.promo_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-dream-violet animate-spin" />
      </div>
    );
  }

  // Calculate totals
  const totalCreditsEarned = credits.reduce((sum, c) => sum + (c.total_earned || 0), 0);
  const totalCreditsAvailable = credits.reduce((sum, c) => sum + (c.available_balance || 0), 0);
  const totalCreditsRedeemed = credits.reduce((sum, c) => sum + (c.total_redeemed || 0), 0);

  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-heading text-xl font-bold text-white">Promo Code Management</h2>
          <p className="text-white/50 text-sm">Create and manage promotional codes</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-dream-violet text-white hover:bg-dream-violet-glow"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Promo Code
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-dream-surface rounded-lg p-4">
          <p className="text-white/50 text-xs mb-1">Active Codes</p>
          <p className="text-white font-mono text-xl">{promoCodes.filter(p => p.status === "active").length}</p>
        </div>
        <div className="bg-dream-surface rounded-lg p-4">
          <p className="text-white/50 text-xs mb-1">Total Earned</p>
          <p className="text-green-400 font-mono text-xl">${totalCreditsEarned.toFixed(2)}</p>
        </div>
        <div className="bg-dream-surface rounded-lg p-4">
          <p className="text-white/50 text-xs mb-1">Available</p>
          <p className="text-dream-violet font-mono text-xl">${totalCreditsAvailable.toFixed(2)}</p>
        </div>
        <div className="bg-dream-surface rounded-lg p-4">
          <p className="text-white/50 text-xs mb-1">Redeemed</p>
          <p className="text-white/60 font-mono text-xl">${totalCreditsRedeemed.toFixed(2)}</p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        {["codes", "credits", "transactions"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveSubTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
              activeSubTab === tab
                ? "bg-dream-violet text-white"
                : "bg-dream-surface text-white/60 hover:bg-white/10"
            }`}
          >
            {tab === "codes" && "Promo Codes"}
            {tab === "credits" && "Credit Balances"}
            {tab === "transactions" && "Transaction Log"}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search..."
          className="pl-10 bg-dream-bg border-dream-border text-white"
        />
      </div>

      {/* Promo Codes Tab */}
      {activeSubTab === "codes" && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dream-border">
                <th className="text-left px-4 py-3 text-white/50 text-sm font-medium">Code</th>
                <th className="text-left px-4 py-3 text-white/50 text-sm font-medium">Holder</th>
                <th className="text-left px-4 py-3 text-white/50 text-sm font-medium">Status</th>
                <th className="text-left px-4 py-3 text-white/50 text-sm font-medium">Created</th>
                <th className="text-right px-4 py-3 text-white/50 text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCodes.map((promo) => (
                <tr key={promo.code} className="border-b border-dream-border hover:bg-white/5">
                  <td className="px-4 py-4">
                    <span className="font-mono text-dream-violet font-bold">{promo.code}</span>
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-white">{promo.holder_name}</p>
                    <p className="text-white/50 text-sm">{promo.holder_email}</p>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      promo.status === "active" 
                        ? "bg-green-500/20 text-green-400" 
                        : "bg-red-500/20 text-red-400"
                    }`}>
                      {promo.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-white/60 text-sm">
                    {new Date(promo.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleToggleStatus(promo.code, promo.status)}
                        className={`p-2 rounded-lg transition-colors ${
                          promo.status === "active"
                            ? "hover:bg-red-500/20 text-red-400"
                            : "hover:bg-green-500/20 text-green-400"
                        }`}
                        title={promo.status === "active" ? "Disable" : "Enable"}
                      >
                        {promo.status === "active" ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleDelete(promo.code)}
                        className="p-2 rounded-lg hover:bg-red-500/20 text-red-400"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredCodes.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-white/50">
                    No promo codes found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Credit Balances Tab */}
      {activeSubTab === "credits" && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dream-border">
                <th className="text-left px-4 py-3 text-white/50 text-sm font-medium">Holder</th>
                <th className="text-left px-4 py-3 text-white/50 text-sm font-medium">Code</th>
                <th className="text-right px-4 py-3 text-white/50 text-sm font-medium">Earned</th>
                <th className="text-right px-4 py-3 text-white/50 text-sm font-medium">Available</th>
                <th className="text-right px-4 py-3 text-white/50 text-sm font-medium">Redeemed</th>
                <th className="text-right px-4 py-3 text-white/50 text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCredits.map((credit) => (
                <tr key={credit.holder_email} className="border-b border-dream-border hover:bg-white/5">
                  <td className="px-4 py-4">
                    <p className="text-white">{credit.holder_name}</p>
                    <p className="text-white/50 text-sm">{credit.holder_email}</p>
                  </td>
                  <td className="px-4 py-4">
                    <span className="font-mono text-dream-violet">{credit.promo_code}</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="font-mono text-green-400">${credit.total_earned?.toFixed(2)}</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="font-mono text-dream-violet font-bold">${credit.available_balance?.toFixed(2)}</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="font-mono text-white/60">${credit.total_redeemed?.toFixed(2)}</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <Button
                      onClick={() => setSelectedHolder(credit)}
                      disabled={credit.available_balance <= 0}
                      size="sm"
                      className="bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      <Gift className="w-3 h-3 mr-1" />
                      Redeem
                    </Button>
                  </td>
                </tr>
              ))}
              {filteredCredits.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-white/50">
                    No credit records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Transaction Log Tab */}
      {activeSubTab === "transactions" && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dream-border">
                <th className="text-left px-4 py-3 text-white/50 text-sm font-medium">Date</th>
                <th className="text-left px-4 py-3 text-white/50 text-sm font-medium">Customer</th>
                <th className="text-left px-4 py-3 text-white/50 text-sm font-medium">Service</th>
                <th className="text-left px-4 py-3 text-white/50 text-sm font-medium">Code</th>
                <th className="text-right px-4 py-3 text-white/50 text-sm font-medium">Original</th>
                <th className="text-right px-4 py-3 text-white/50 text-sm font-medium">Discount</th>
                <th className="text-right px-4 py-3 text-white/50 text-sm font-medium">Credit</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx.id} className="border-b border-dream-border hover:bg-white/5">
                  <td className="px-4 py-4 text-white/60 text-sm">
                    {new Date(tx.date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-white text-sm">{tx.customer_name}</p>
                    <p className="text-white/50 text-xs">{tx.customer_email}</p>
                  </td>
                  <td className="px-4 py-4 text-white/80 text-sm">{tx.service_name}</td>
                  <td className="px-4 py-4">
                    <span className="font-mono text-dream-violet text-sm">{tx.promo_code}</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="font-mono text-white/60">${tx.original_price?.toFixed(2)}</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="font-mono text-red-400">-${tx.discount_applied?.toFixed(2)}</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="font-mono text-green-400">+${tx.credit_earned?.toFixed(2)}</span>
                  </td>
                </tr>
              ))}
              {transactions.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-white/50">
                    No transactions yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreatePromoCodeModal
          onClose={() => setShowCreateModal(false)}
          onCreated={fetchData}
        />
      )}
      {selectedHolder && (
        <RedeemCreditModal
          holder={selectedHolder}
          onClose={() => setSelectedHolder(null)}
          onRedeemed={fetchData}
        />
      )}
    </div>
  );
}
